import { cn } from "@/lib/utils/cn";
import { Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import {
  ColumnDef,
  OnChangeFn,
  SortingState,
  Table as TanstackTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

type DataTable2BaseProps<T> = {
  onRowClick?: (row: T) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
  maxHeight?: number | string;
  minHeight?: number | string;
  loading?: boolean;
  loadingText?: string;
};

type DataTable2ControlledProps<T> = DataTable2BaseProps<T> & {
  table: TanstackTable<T>;
  columns?: never;
  data?: never;
  manualSorting?: never;
  sorting?: never;
  onSortingChange?: never;
};

type DataTable2UncontrolledProps<T> = DataTable2BaseProps<T> & {
  table?: never;
  columns: ColumnDef<T>[];
  data: T[];
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
};

export type DataTable2Props<T> = DataTable2ControlledProps<T> | DataTable2UncontrolledProps<T>;

export function DataTable2<T>({
  columns: columnsProp,
  data: dataProp,
  table: tableProp,
  onRowClick,
  emptyTitle = "Keine Ergebnisse",
  emptyDescription = "Passe deine Suche an oder versuche es später erneut.",
  manualSorting = false,
  sorting,
  onSortingChange,
  className,
  maxHeight,
  minHeight,
  loading = false,
  loadingText = "Lädt Daten...",
}: DataTable2Props<T>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  const resolvedSorting = sorting ?? internalSorting;
  const resolvedOnSortingChange = onSortingChange ?? setInternalSorting;

  const sortedRowModel = useMemo(() => (manualSorting ? undefined : getSortedRowModel()), [manualSorting]);

  const internalTable = useReactTable({
    data: (dataProp ?? []) as T[],
    columns: (columnsProp ?? []) as ColumnDef<T>[],
    state: { sorting: resolvedSorting },
    onSortingChange: resolvedOnSortingChange,
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortedRowModel,
  });

  const table = tableProp ?? internalTable;

  const leafColumnCount = table.getAllLeafColumns().length;

  return (
    <div className={cn("rounded-xl border border-gray-200 bg-white overflow-hidden", className)}>
      <div className="relative overflow-auto" style={{ ...(maxHeight ? { maxHeight } : {}), ...(minHeight ? { minHeight } : {}) }}>
        <Table>
          <TableHeader className="bg-white sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b border-gray-100">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="h-11 px-4 text-xs font-medium text-gray-500">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(
                    "border-b border-gray-100 last:border-b-0 transition-colors",
                    onRowClick ? "cursor-pointer hover:bg-gray-50" : "hover:bg-gray-50"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-4 py-3.5 align-middle text-sm text-gray-900">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={leafColumnCount} className="px-4 py-16">
                  <div className="flex flex-col items-center text-center gap-1">
                    <div className="text-sm font-medium text-gray-900">{emptyTitle}</div>
                    <div className="text-sm text-gray-500">{emptyDescription}</div>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {loading ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/72 backdrop-blur-[1px]">
            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingText}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
